import { Link, NavLink } from 'react-router-dom';
import { LogOut, Phone, Moon, Sun, Shield, HelpCircle, Menu } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { OrgSwitcher } from '@/components/OrgSwitcher';
import { cn } from '@/lib/utils';
import type { SessionUser } from '@/lib/api';
import { useState, useEffect } from 'react';

const navItems = [
  { to: '/dashboard', label: 'לוח בקרה', end: true },
  { to: '/customers', label: 'לקוחות' },
  { to: '/questionnaires', label: 'שאלונים' },
  { to: '/cycles', label: 'מחזורים' },
  { to: '/records', label: 'רשומות' },
  { to: '/settings', label: 'הגדרות' }
];

export function Header({ user }: { user: SessionUser }) {
  const [theme, setTheme] = useState<'light' | 'dark'>(() =>
    document.documentElement.classList.contains('dark') ? 'dark' : 'light'
  );

  useEffect(() => {
    if (theme === 'dark') document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
    localStorage.setItem('easy_call_theme', theme);
  }, [theme]);

  return (
    <header className="sticky top-0 z-40 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto flex h-14 items-center gap-1 px-4">
        {/* Mobile nav menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="md:hidden" aria-label="תפריט">
              <Menu className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-[12rem]">
            {navItems.map((item) => (
              <DropdownMenuItem key={item.to} asChild>
                <NavLink to={item.to} end={item.end}>
                  {item.label}
                </NavLink>
              </DropdownMenuItem>
            ))}
            {user.isSuperAdmin && (
              <DropdownMenuItem asChild>
                <NavLink to="/admin" className="gap-2">
                  <Shield className="h-3.5 w-3.5" />
                  ניהול ארגונים
                </NavLink>
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/help" className="gap-2">
                <HelpCircle className="h-4 w-4" />
                עזרה
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <Link to="/dashboard" className="flex items-center gap-2 me-2 md:me-6">
          <div className="rounded-md bg-primary/10 p-1.5">
            <Phone className="h-5 w-5 text-primary" />
          </div>
          <span className="hidden font-heading text-lg font-semibold tracking-tight sm:inline-block">
            Easy Call
          </span>
        </Link>

        <nav className="hidden items-center gap-1 text-sm md:flex">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  'rounded-md px-3 py-1.5 transition-colors',
                  isActive
                    ? 'bg-accent text-accent-foreground font-medium'
                    : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
          {user.isSuperAdmin && (
            <NavLink
              to="/admin"
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-1 rounded-md px-3 py-1.5 transition-colors',
                  isActive
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
                )
              }
            >
              <Shield className="h-3.5 w-3.5" />
              ניהול ארגונים
            </NavLink>
          )}
        </nav>

        <div className="ms-auto flex items-center gap-1 sm:gap-2">
          <OrgSwitcher />

          <Button asChild variant="ghost" size="icon" aria-label="עזרה" className="hidden sm:inline-flex">
            <Link to="/help">
              <HelpCircle className="h-4 w-4" />
            </Link>
          </Button>

          <Button
            variant="ghost"
            size="icon"
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            aria-label="החלף ערכת נושא"
          >
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-2">
                {user.picture ? (
                  <img src={user.picture} alt="" className="h-6 w-6 rounded-full" />
                ) : (
                  <div className="h-6 w-6 rounded-full bg-primary/20" />
                )}
                <span className="hidden text-sm sm:inline">{user.name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-[12rem]">
              <DropdownMenuLabel className="font-normal">
                <div className="text-xs text-muted-foreground">{user.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <a href="/auth/logout" className="gap-2">
                  <LogOut className="h-4 w-4" />
                  התנתק
                </a>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
